import { labelForField } from './fieldMetadata';

/**
 * Resolves the human-readable label for an Ask-next chip given an engine
 * field path.
 *
 * The schema-walker label map (`fieldLabels`) only stores paths with `[0]`
 * for arrays (e.g. `procedure[0].coverage.oopmax_applies`). The engine emits
 * the actual array index from the data (e.g. `procedure[1]....`), so we
 * normalize indices to `[0]` for the lookup, then suffix the resolved label
 * with the human position (`(#2)`) when any index is non-zero — this
 * disambiguates chips for multi-item drug/procedure arrays.
 *
 * @param key  Engine field path, e.g. `procedure[1].coverage.oopmax_applies`.
 * @param fieldLabels  Optional schema-derived label map keyed by `[0]`-form
 *                     path. Falls back to the hardcoded `FIELD_LABELS` map,
 *                     and finally to the raw key if nothing matches.
 * @returns Display label, with a `(#N)` position suffix for non-zero array
 *          indices when a label was found. Returns the raw `key` when no
 *          mapping exists.
 *
 * @example
 *   resolveAskNextLabel('procedure[0].coverage.oopmax_applies', map)
 *     // → 'Procedure Details — OOP Max Applies'
 *   resolveAskNextLabel('procedure[1].coverage.oopmax_applies', map)
 *     // → 'Procedure Details — OOP Max Applies (#2)'
 *   resolveAskNextLabel('unknown[1].field')
 *     // → 'unknown[1].field'
 */
export function resolveAskNextLabel(key: string, fieldLabels?: Record<string, string>): string {
  const normalized = key.replace(/\[\d+\]/g, '[0]');
  const baseLabel = fieldLabels?.[normalized] ?? labelForField(normalized);
  if (baseLabel === normalized) return key;
  const positions = [...key.matchAll(/\[(\d+)\]/g)].map(m => Number(m[1])).filter(i => i > 0);
  if (positions.length === 0) return baseLabel;
  return `${baseLabel} (${positions.map(i => `#${i + 1}`).join(' ')})`;
}
